import React, { useEffect, useState } from "react";
import { useForm, useFormContext, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import { FaInfoCircle } from "react-icons/fa";
import { store } from "../../Store/store";
import * as HVTRegistrationService from "../../Services/HVTRegistration/HVTRegistrationService";
import { formatDateDDMMYYYY, formatDateTimeSlashed } from "./HVTCommonHelpers";
import { DEFAULT_VALUES, getStepFields } from "./RegistrationForm/wizardConfig";
import StepIndicator from "./RegistrationForm/StepIndicator";
import StepFooterNav from "./RegistrationForm/StepFooterNav";
import PatientDetailsSection from "./RegistrationForm/PatientDetailsSection";
import TestsSection from "./RegistrationForm/TestsSection";
import SlotSection from "./RegistrationForm/SlotSection";
import BookingReviewStep from "./RegistrationForm/BookingReviewStep";
import CommonConfirmationModal from "../../Common/CommonConfirmationModal";
import { useUnsavedChangesGuard } from "./useUnsavedChangesGuard";

const ADD_PATIENT_STEPS = [
    { id: "patient", label: "Patient" },
    { id: "tests", label: "Tests" },
    { id: "slot", label: "Slot" },
    { id: "summary", label: "Summary" },
];

const deriveSlotTime = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${hh}:${mm}:${ss}`;
};

const HVTAddPatientModalInner = ({ item, siblingCount, onClose, onAdded, onSubmittingChange }) => {
    const userInfo = store.getState();
    const accountDetails = userInfo?.Partner_Account_Details;
    const isCashClient = accountDetails?.MHL_Payment_Type__c === "Cash";

    const { watch, setValue, trigger } = useFormContext();

    const [step, setStep] = useState("patient");
    const [salutationOptions, setSalutationOptions] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        onSubmittingChange?.(isSubmitting);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSubmitting]);

    const gender = watch("gender");
    const salutation = watch("salutation");
    const firstName = watch("firstName");
    const lastName = watch("lastName");
    const mobile = watch("mobile");
    const email = watch("email");
    const dob = watch("dob");
    const visitDate = watch("visitDate");
    const pincode = watch("pincode");
    const locationId = watch("locationId");
    const locationName = watch("locationName");
    const orgId = watch("orgId");
    const addressLine = watch("addressLine") || "";
    const tests = watch("tests") || [];
    const selectedSlot = watch("selectedSlot");

    const gross = tests.reduce((s, t) => s + (parseFloat(t.TEST_AMOUNT) || 0), 0);
    const discount = tests.reduce((s, t) => s + (parseFloat(t.TEST_DISCOUNT) || 0), 0);
    const net = gross - discount;

    useEffect(() => {
        setValue("orgId", userInfo?.selectedOrganization?.MHL_Org_Id__c || "");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        HVTRegistrationService.GetSalutationList()
            .then(({ data }) => {
                setSalutationOptions(data?.IsSuccess && data?.Data?.length ? data.Data : []);
            })
            .catch(() => setSalutationOptions([]));
    }, []);

    const handlePatientNext = async () => {
        const valid = await trigger(getStepFields("patient"));
        if (!valid) return;
        setStep("tests");
    };

    const handleTestsNext = async () => {
        const valid = await trigger(getStepFields("tests"));
        if (!valid) return;
        setStep("slot");
    };

    const handleSlotNext = async () => {
        const valid = await trigger(getStepFields("slot"));
        if (!valid) return;
        setStep("summary");
    };

    const handleSubmitPatient = () => {
        setIsSubmitting(true);

        HVTRegistrationService.GenerateUniquePatientId()
            .then(({ data: patientIdResp }) => {
                if (!patientIdResp?.IsSuccess || !patientIdResp?.Data) {
                    toast.error(patientIdResp?.ResponseMessage || "Unable to generate a patient ID. Please try again.");
                    return null;
                }
                const patientId = patientIdResp.Data;

                const payload = {
                    PbookingId: item.BookingId,
                    Patientid: patientId,
                    CallSource: "Partner Portal",
                    IsMyClinic: !!item.IsMyClinic,
                    Scheduledate: selectedSlot?.Slot_TIME || "",
                    Bookingdate: `${formatDateDDMMYYYY(visitDate)} ${selectedSlot?.Slot_TIME || ""}`,
                    Mobile: mobile,
                    Clientid: "COC020523",
                    Organizationid: "",
                    Titleid: salutation,
                    FirstName: firstName.trim(),
                    LastName: lastName.trim(),
                    DOB: formatDateDDMMYYYY(dob),
                    Genderid: gender,
                    Emailid: email ? email.trim() : "",
                    Height: "",
                    Weight: "",
                    AddressLine: (addressLine ? `${addressLine}, ${pincode}` : pincode).trim(),
                    Pincodeid: pincode,
                    LocationName: locationName || "",
                    Circleid: "",
                    Phleboid: "",
                    orgid: orgId || "",
                    LocationID: locationId || "",
                    Notification: "SMS,Whatsapp",
                    DiscountType: "No Discount",
                    PromoCode: "",
                    DocumentType: "",
                    DocumentNumber: "",
                    Loyalty: "0",
                    Wallet: "0",
                    HomeCollectionModel: tests,
                    TotalReceivables: [
                        {
                            GrossAmount: gross,
                            TotalDiscountAmount: discount,
                            NetAmount: net,
                            AmountReceived: isCashClient ? 0 : net,
                            BalanceReceivable: isCashClient ? net : 0,
                        },
                    ],
                    PaymentDetails: [
                        {
                            order_id: "",
                            tracking_id: "",
                            bank_ref_no: "",
                            order_status: "Success",
                            failure_message: "",
                            payment_mode: isCashClient ? "pay_at_pickup" : "UPI",
                            card_name: "",
                            status_code: "",
                            status_message: "",
                            currency: "INR",
                            amount: (isCashClient ? 0 : net).toFixed(2),
                            billing_name: "",
                            billing_address: "",
                            billing_city: "",
                            billing_state: "",
                            billing_zip: "",
                            billing_country: "",
                            billing_tel: "",
                            billing_email: "",
                            delivery_name: "",
                            delivery_address: "",
                            delivery_city: "",
                            delivery_state: "",
                            delivery_zip: "",
                            delivery_country: "",
                            delivery_tel: "",
                            merchant_param1: "",
                            merchant_param2: "",
                            merchant_param3: "",
                            merchant_param4: "",
                            merchant_param5: "",
                            vault: "",
                            offer_type: "",
                            offer_code: "",
                            discount_value: 0,
                            mer_amount: (isCashClient ? 0 : net).toFixed(2),
                            eci_value: "",
                            retry: "",
                            response_code: "",
                            billing_notes: "",
                            trans_date: formatDateTimeSlashed(new Date()),
                            bin_country: "",
                        },
                    ],
                };

                return HVTRegistrationService.AddNewRegistrationMultiPatient([payload]).then((regResp) => {
                    if (!regResp?.data?.IsSuccess) {
                        toast.error(regResp?.data?.Message || "Unable to add this patient.");
                        return;
                    }

                    toast.success("Patient added successfully.");
                    onAdded();
                    onClose();
                });
            })
            .catch(() => toast.error("Something went wrong while adding this patient."))
            .finally(() => setIsSubmitting(false));
    };

    const handleConfirmClick = async () => {
        const valid = await trigger();
        if (!valid) {
            toast.error("Please review the form — some details are missing or invalid.");
            return;
        }
        handleSubmitPatient();
    };

    const footerConfig = {
        patient: { showBack: false, onNext: handlePatientNext },
        tests: { onBack: () => setStep("patient"), onNext: handleTestsNext },
        slot: { onBack: () => setStep("tests"), onNext: handleSlotNext },
        summary: {
            onBack: () => setStep("slot"),
            onNext: handleConfirmClick,
            nextLabel: "Submit",
            isNextLoading: isSubmitting,
            backDisabled: isSubmitting,
        },
    }[step];

    return (
        <>
            <div className="hvt-wizard-scroll">
                <StepIndicator steps={ADD_PATIENT_STEPS} currentStepId={step} />

                {step === "patient" && (
                    <div className="hvt-step-body">
                        <PatientDetailsSection salutationOptions={salutationOptions} />
                    </div>
                )}

                {step === "tests" && (
                    <div className="hvt-step-body">
                        <TestsSection />
                        <div className="hvt-step-note">
                            <FaInfoCircle size={13} />
                            Search by test name or code, or pick from the frequently booked tests above.
                        </div>
                    </div>
                )}

                {step === "slot" && (
                    <div className="hvt-step-body">
                        <SlotSection
                            primaryBookingId={item.BookingId}
                            slots={[]}
                            slotsMessage=""
                            isLoadingSlots={false}
                            onSelectSlot={() => {}}
                        />
                        <div className="hvt-step-note">
                            <FaInfoCircle size={13} />
                            Additional patients in the same group booking share the primary patient's visit slot.
                        </div>
                    </div>
                )}

                {step === "summary" && (
                    <div className="hvt-step-body">
                        <BookingReviewStep primaryBookingId={item.BookingId} />
                    </div>
                )}

            </div>

            {footerConfig && (
                <div className="hvt-wizard-footer">
                    <StepFooterNav {...footerConfig} />
                </div>
            )}
        </>
    );
};

const HVTAddPatientModal = ({ item, preloadedBooking, siblingCount, onClose, onAdded }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const methods = useForm({
        mode: "onChange",
        defaultValues: {
            ...DEFAULT_VALUES,
            pincode: preloadedBooking?.Pincode || "",
            locationId: preloadedBooking?.LocationId || "",
            locationName: preloadedBooking?.Location || "",
            addressLine: preloadedBooking?.AddressLine || "",
            addressLine1: preloadedBooking?.AddressLine || "",
            visitDate: preloadedBooking?.VisitDateTime ? new Date(preloadedBooking.VisitDateTime) : new Date(),
            selectedSlot: preloadedBooking?.VisitDateTime
                ? { Slot_TIME: deriveSlotTime(preloadedBooking.VisitDateTime) }
                : null,
        },
    });

    const isDirty = methods.formState.isDirty;

    useUnsavedChangesGuard(isDirty && !isSubmitting, onClose);

    const handleCloseClick = () => {
        if (isSubmitting) return;
        if (isDirty) {
            setShowConfirmationModal(true);
        } else {
            onClose();
        }
    };

    return (
        <div className="modal-backdrop">
            <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                <div className="modal-dialog modal-dialog-centered hvt-modal-dialog hvt-modal-dialog-wide" role="document">
                    <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                        <FormProvider {...methods}>
                            <div className="hvt-wizard-modal">
                                <div className="modal-header px-3 py-2">
                                    <h5 className="modal-title">Add Patient ({item.BookingId})</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={handleCloseClick} disabled={isSubmitting}></button>
                                </div>
                                <HVTAddPatientModalInner
                                    item={item}
                                    siblingCount={siblingCount}
                                    onClose={onClose}
                                    onAdded={onAdded}
                                    onSubmittingChange={setIsSubmitting}
                                />
                            </div>
                        </FormProvider>
                    </div>

                    {showConfirmationModal && (
                        <CommonConfirmationModal
                            setShowConfirmationModal={setShowConfirmationModal}
                            description="You have unsaved patient details. Are you sure you want to close?"
                            onConfirm={() => {
                                setShowConfirmationModal(false);
                                onClose();
                            }}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default HVTAddPatientModal;
